import React, { useState, useEffect, useRef } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Link } from 'react-router-dom';

import api from '../api';
import ActivityFeed from '../components/ActivityFeed';
import LoadingSpinner from '../components/LoadingSpinner';
import logoImg from '../assets/logo.png';
import { useAuth } from '../contexts/AuthContext';
import { solscanAccountUrl } from '../network';

function formatUsd(usd) {
    if (!usd || usd === 0) return '$0.00';
    for (let d = 2; d <= 8; d++) {
        if (parseFloat(usd.toFixed(d)) !== 0) {
            return `$${usd.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })}`;
        }
    }
    return '<$0.000000001';
}

// Truncate a wallet address: first 6 chars + … + last 6 chars
function truncateWallet(addr) {
    if (!addr) return null;
    if (addr.length <= 16) return addr;
    return addr.slice(0, 6) + '…' + addr.slice(-6);
}

function WalletBalanceChart({ cardStyle }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        api.get('/wallet/balance-history')
            .then(res => { setData(res.data); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    if (loading) return null;
    if (!data.length || data.every(d => (d.balance_usdc ?? 0) < 0.000001)) return null;

    const chartData = data.map(d => ({
        date: d.date ? d.date.slice(5) : '',
        value: d.balance_usdc ?? 0,
    }));

    return (
        <div style={{ ...cardStyle, marginBottom: '24px' }}>
            <div
                onClick={() => setExpanded(v => !v)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', cursor: 'pointer', marginBottom: expanded ? '16px' : 0 }}
            >
                <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    Balance History
                    <span style={{ marginLeft: '8px', fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)', fontWeight: 400 }}>
                        {expanded ? '▲ hide' : '▼ show'}
                    </span>
                </h3>
            </div>
            {expanded && (
                <ResponsiveContainer width="100%" height={180}>
                    <AreaChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="balGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#ffc107" stopOpacity={0.25} />
                                <stop offset="95%" stopColor="#ffc107" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                        <YAxis tickFormatter={v => `$${v.toFixed(2)}`} tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }} tickLine={false} axisLine={false} width={60} />
                        <Tooltip
                            formatter={v => [`$${v.toFixed(2)} USDC`]}
                            labelFormatter={l => `Date: ${l}`}
                            contentStyle={{ background: 'rgba(18,18,26,0.95)', border: '1px solid rgba(255,193,7,0.25)', borderRadius: '10px', fontSize: '0.8rem' }}
                            labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
                            itemStyle={{ color: '#ffc107' }}
                        />
                        <Area type="monotone" dataKey="value" stroke="#ffc107" strokeWidth={2} fill="url(#balGrad)" dot={false} />
                    </AreaChart>
                </ResponsiveContainer>
            )}
        </div>
    );
}

function Dashboard() {
    const { user } = useAuth();

    const [userName, setUserName] = useState('User');
    const [copied, setCopied] = useState(false);
    const [solPrice, setSolPrice] = useState(null);
    const [balance, setBalance] = useState(0);
    const [dailySol, setDailySol] = useState(null);
    const [giveBackLoading, setGiveBackLoading] = useState(false);
    const [giveBackDone, setGiveBackDone] = useState(false);
    const [showGiveBackTooltip, setShowGiveBackTooltip] = useState(false);
    const [showKycTooltip, setShowKycTooltip] = useState(false);
    const [balanceLoading, setBalanceLoading] = useState(false);

    const walletAddress = user?.wallet_address || null;
    const prevBalanceRef = useRef(null);

    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago';

    const fetchBalance = async (isPolling = false) => {
        if (!walletAddress) return;
        if (!isPolling) setBalanceLoading(true);
        try {
            const res = await api.get('/balance');
            const newBalance = res.data.balance ?? 0;
            prevBalanceRef.current = newBalance;
            setBalance(newBalance);
        } catch (err) {
            console.error('Balance fetch error:', err.message);
        } finally {
            if (!isPolling) setBalanceLoading(false);
        }
    };

    const fetchDailySol = async () => {
        try {
            const res = await api.get(`/balance/daily?timezone=${encodeURIComponent(userTimezone)}`);
            setDailySol(res.data.daily_sol ?? 0);
            if (res.data.given_back_today) setGiveBackDone(true);
        } catch (err) {
            console.error('Daily balance error:', err.message);
        }
    };

    const handleGiveBack = async () => {
        setGiveBackLoading(true);
        try {
            await api.post('/balance/giveback', { timezone: userTimezone });
            setDailySol(0);
            setGiveBackDone(true);
            fetchBalance();
        } catch (err) {
            alert(err.response?.data?.error || 'Give back failed. Please try again.');
        } finally {
            setGiveBackLoading(false);
        }
    };

    const fetchPrice = async () => {
        // solPrice still used by ActivityFeed for legacy SOL records
        api.get('/sol-price').catch(() => {}).then(res => {
            if (res?.data?.price) setSolPrice(res.data.price);
        });
    };

    useEffect(() => {
        if (user) {
            setUserName(user.given_name || user.name || 'User');
        }
        fetchPrice();
        fetchBalance();
        fetchDailySol();

        // Poll balance every 60s
        const pollInterval = setInterval(() => fetchBalance(true), 60_000);
        return () => clearInterval(pollInterval);
    }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    const copyWallet = () => {
        if (!walletAddress) return;
        navigator.clipboard.writeText(walletAddress).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    // Card style matching login/register glassmorphism
    const cardStyle = {
        background: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '20px',
        backdropFilter: 'blur(20px)',
        padding: '24px',
        marginBottom: '24px',
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(180deg, #12121a 0%, #1a1a2e 50%, #12121a 100%)',
            color: '#ffffff',
            position: 'relative',
            overflow: 'hidden',
        }}>

            {/* Gradient orbs for visual interest */}
            <div style={{
                position: 'fixed',
                top: '-20%',
                right: '-10%',
                width: '500px',
                height: '500px',
                background: 'radial-gradient(circle, rgba(255, 193, 7, 0.12) 0%, transparent 70%)',
                borderRadius: '50%',
                pointerEvents: 'none',
                zIndex: 0,
            }} />
            <div style={{
                position: 'fixed',
                bottom: '-30%',
                left: '-10%',
                width: '400px',
                height: '400px',
                background: 'radial-gradient(circle, rgba(255, 193, 7, 0.08) 0%, transparent 70%)',
                borderRadius: '50%',
                pointerEvents: 'none',
                zIndex: 0,
            }} />

            {/* Main content */}
            <div style={{
                maxWidth: '900px',
                margin: '0 auto',
                padding: '40px 20px',
                position: 'relative',
                zIndex: 10,
            }}>
                {/* Greeting */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px', flexWrap: 'wrap' }}>
                    <h1 style={{
                        margin: 0,
                        fontSize: 'clamp(1.5rem, 5vw, 2rem)',
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.7) 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                    }}>
                        Welcome to You-Bee, {userName}
                    </h1>
                    {user?.kyc_status === 'Approved' && (
                        <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}
                            onMouseEnter={() => setShowKycTooltip(true)}
                            onMouseLeave={() => setShowKycTooltip(false)}
                        >
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: '30px', height: '30px', borderRadius: '50%',
                                border: '2px solid #22c55e', background: 'rgba(34,197,94,0.12)',
                                color: '#22c55e', fontSize: '1rem', fontWeight: 700,
                                cursor: 'default',
                            }}>✔</span>
                            {showKycTooltip && (
                                <span style={{
                                    position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%',
                                    transform: 'translateX(-50%)',
                                    background: 'rgba(20,20,35,0.97)', border: '1px solid rgba(34,197,94,0.4)',
                                    color: '#22c55e', fontSize: '0.75rem', fontWeight: 600,
                                    borderRadius: '8px', padding: '5px 12px', whiteSpace: 'nowrap',
                                    pointerEvents: 'none', zIndex: 10,
                                }}>KYC Verified</span>
                            )}
                        </span>
                    )}
                </div>

                {/* --- BALANCE CARD --- */}
                <div style={{
                    background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 152, 0, 0.1) 100%)',
                    border: '1px solid rgba(255, 193, 7, 0.2)',
                    borderRadius: '24px',
                    padding: '32px',
                    marginBottom: '24px',
                    position: 'relative',
                    backdropFilter: 'blur(20px)',
                    overflow: 'hidden',
                }}>
                    {/* Subtle glow effect */}
                    <div style={{
                        position: 'absolute',
                        top: '-50%',
                        right: '-20%',
                        width: '300px',
                        height: '300px',
                        background: 'radial-gradient(circle, rgba(255, 193, 7, 0.2) 0%, transparent 70%)',
                        borderRadius: '50%',
                        pointerEvents: 'none',
                    }} />

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', position: 'relative' }}>
                        <h2 style={{ margin: 0, opacity: 0.7, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 600 }}>
                            Wallet Balance
                        </h2>
                    </div>

                    <h1 style={{
                        fontSize: '3.5rem',
                        margin: '16px 0',
                        fontWeight: 700,
                        position: 'relative',
                    }}>
                        {balanceLoading ? (
                            <span style={{ fontSize: '1.5rem', opacity: 0.5 }}>Loading…</span>
                        ) : (
                            <>
                                ${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                                <span style={{ fontSize: '1.25rem', color: '#ffc107' }}>USDC</span>
                            </>
                        )}
                    </h1>

                    {/* Daily UBI earned today */}
                    {dailySol !== null && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                background: 'rgba(34,197,94,0.1)',
                                border: '1px solid rgba(34,197,94,0.25)',
                                borderRadius: '12px',
                                padding: '8px 16px',
                                fontSize: '0.85rem',
                            }}>
                                <span style={{ color: '#22c55e', fontWeight: 700, fontSize: '1rem' }}>▲</span>
                                <span style={{ color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>Today's UBI</span>
                                <span style={{ color: '#22c55e', fontWeight: 700 }}>
                                    {dailySol > 0
                                        ? `+$${dailySol.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} USDC`
                                        : '—'}
                                </span>
                            </div>
                            {giveBackDone ? (
                                <span style={{ fontSize: '0.85rem', color: '#ffc107', fontWeight: 600 }}>
                                    🐝 Thanks for giving back!
                                </span>
                            ) : !user?.auto_giveback && dailySol > 0 && balance > dailySol && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <button
                                        onClick={handleGiveBack}
                                        disabled={giveBackLoading}
                                        style={{
                                            padding: '8px 16px',
                                            borderRadius: '12px',
                                            border: '1px solid rgba(255,193,7,0.35)',
                                            background: 'rgba(255,193,7,0.08)',
                                            color: '#ffc107',
                                            fontWeight: 600,
                                            fontSize: '0.85rem',
                                            cursor: giveBackLoading ? 'not-allowed' : 'pointer',
                                            opacity: giveBackLoading ? 0.6 : 1,
                                            transition: 'all 0.2s',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {giveBackLoading ? 'Sending…' : '🐝 Give Back Today\'s UBI'}
                                    </button>
                                    <div style={{ position: 'relative', display: 'inline-flex' }}>
                                        <span
                                            onMouseEnter={() => setShowGiveBackTooltip(true)}
                                            onMouseLeave={() => setShowGiveBackTooltip(false)}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '18px',
                                                height: '18px',
                                                borderRadius: '50%',
                                                border: '1px solid rgba(255,255,255,0.25)',
                                                color: 'rgba(255,255,255,0.45)',
                                                fontSize: '0.7rem',
                                                fontWeight: 700,
                                                cursor: 'default',
                                                userSelect: 'none',
                                                flexShrink: 0,
                                            }}
                                        >?</span>
                                        {showGiveBackTooltip && (
                                            <div style={{
                                                position: 'absolute',
                                                bottom: '26px',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                background: 'rgba(20,20,32,0.97)',
                                                border: '1px solid rgba(255,193,7,0.25)',
                                                borderRadius: '10px',
                                                padding: '10px 14px',
                                                fontSize: '0.78rem',
                                                color: 'rgba(255,255,255,0.8)',
                                                whiteSpace: 'nowrap',
                                                zIndex: 100,
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                                                pointerEvents: 'none',
                                            }}>
                                                Don't need today's distribution?<br />
                                                Give it back to the community hive!
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}


                    {/* Wallet address */}
                    {walletAddress && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', position: 'relative', marginBottom: '20px' }}>
                            <span style={{ opacity: 0.5, fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase' }}>
                                Wallet
                            </span>
                            <span
                                title="This is your individual custodial Solana wallet. Here you can verify and monitor your balance, which you can withdraw after completing identity verification."
                                style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: '16px', height: '16px', borderRadius: '50%',
                                    background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)',
                                    color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', fontWeight: 700,
                                    cursor: 'help', flexShrink: 0, lineHeight: 1,
                                }}
                            >?</span>
                            <code style={{
                                fontSize: '0.85rem',
                                opacity: 0.8,
                                letterSpacing: '0.5px',
                                background: 'rgba(255, 255, 255, 0.05)',
                                padding: '4px 10px',
                                borderRadius: '6px',
                            }}>
                                {truncateWallet(walletAddress)}
                            </code>
                            <button
                                onClick={copyWallet}
                                title="Copy full address"
                                style={{
                                    background: copied ? 'rgba(34, 197, 94, 0.2)' : 'rgba(255,255,255,0.08)',
                                    border: copied ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(255,255,255,0.15)',
                                    color: copied ? '#22c55e' : 'rgba(255,255,255,0.7)',
                                    borderRadius: '6px',
                                    padding: '4px 12px',
                                    fontSize: '0.75rem',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    transition: 'all 0.2s',
                                }}
                            >
                                {copied ? 'Copied!' : 'Copy'}
                            </button>
                            {walletAddress && (
                                <a
                                    href={solscanAccountUrl(walletAddress)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        background: 'rgba(255,255,255,0.08)',
                                        border: '1px solid rgba(255,255,255,0.15)',
                                        color: 'rgba(255,255,255,0.7)',
                                        borderRadius: '6px',
                                        padding: '4px 12px',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        textDecoration: 'none',
                                        transition: 'all 0.2s',
                                    }}
                                >
                                    Solscan ↗
                                </a>
                            )}
                        </div>
                    )}

                </div>

                {/* Balance History Chart */}
                <WalletBalanceChart cardStyle={cardStyle} />

                {/* Activity Feed */}
                <ActivityFeed cardStyle={cardStyle} solPrice={solPrice} />

                {/* --- ABOUT SECTION --- */}
                <div style={{
                    ...cardStyle,
                    marginTop: '24px',
                    lineHeight: '1.8',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
                        <img
                            src={logoImg}
                            alt="You-Bee Logo"
                            style={{
                                width: '48px',
                                height: '48px',
                                objectFit: 'contain',
                                borderRadius: '50%',
                                border: '2px solid rgba(255, 193, 7, 0.3)',
                                background: 'rgba(255, 255, 255, 0.05)',
                                padding: '4px',
                            }}
                        />
                        <h3 style={{ margin: 0, color: '#ffc107', fontSize: '1.25rem', fontWeight: 700 }}>About You-Bee</h3>
                    </div>
                    <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginTop: 0 }}>
                        You-Bee is a next-generation Universal Basic Income (UBI) platform powered by blockchain technology.
                    </p>
                    <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                        Unlike traditional aid programs, You-Bee removes intermediaries, allowing Worker Bees to fund targeted community hives directly, allowing funds to flow directly from contributors to targeted community hives, ranging from geographical to professional.
                    </p>
                    <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                        You-Bee recipients can qualify for more than one distribution at any time, based on their verified information.
                    </p>
                    <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                        By leveraging the transparency of the Solana blockchain, every transaction is verifiable, ensuring that 100% of contributions reach their intended destination efficiently and securely.
                    </p>
                    <p style={{
                        color: '#ffc107',
                        fontWeight: 600,
                        marginBottom: 0,
                        paddingTop: '8px',
                        borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                        marginTop: '16px',
                    }}>
                        In the era of AI-related job displacement, everyone can use a little You-Bee!
                    </p>
                </div>
            </div>

            {/* Footer */}
            <div style={{
                borderTop: '1px solid rgba(255,255,255,0.06)',
                marginTop: '16px',
                padding: '24px 20px',
                textAlign: 'center',
                color: 'rgba(255,255,255,0.3)',
                fontSize: '0.8rem',
            }}>
                <div style={{ marginBottom: '4px' }}>
                    © {new Date().getFullYear()} You-Bee. All rights reserved.
                </div>
                <div style={{ marginBottom: '8px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.2)' }}>
                    Patent Pending
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', flexWrap: 'wrap' }}>
                    <Link to="/privacy" style={{ color: 'rgba(255,193,7,0.5)', textDecoration: 'none' }}>Privacy Policy</Link>
                    <Link to="/terms" style={{ color: 'rgba(255,193,7,0.5)', textDecoration: 'none' }}>Terms of Service</Link>
                    <a href="mailto:support@you-bee.com" style={{ color: 'rgba(255,193,7,0.5)', textDecoration: 'none' }}>Contact Support</a>
                </div>
            </div>
        </div>
    );
}

export default Dashboard;
